import { TOOL_NAMES } from 'js/src/agent/toolNames';
import chatMessageTextStyles from 'css/src/components/agent/ChatMessageText.module.scss';

import React from 'react';
import { fromJS as ImmutableFromJS } from 'immutable';
import { VideoData } from '@lumen5/luminary';

import { z } from 'zod';

import MarkdownWithDiff from '../components/MarkdownWithDiff';
import type { CustomRendererComponentProps, ToolExecutionContext } from '../types';

import { resolveSceneHandle } from './scene-addressing';
import { VideoStateTool } from './VideoStateTool';

import type { SlideBackgroundModel } from 'js/src/client/types.gen';
import { DEFAULT_STICKER_SOURCE_DATA, type ImmutableMap } from 'js/src/libs/consts';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { setMediaAssetForSceneOperation } from 'js/src/luminary/luminary-operations';

export const addStickerSchema = z.object({
    scene_id: z.string().describe('The scene_id from the video state.'),
});
export type AddStickerArgs = z.infer<typeof addStickerSchema>;

export class AddStickerTool extends VideoStateTool<typeof addStickerSchema> {
    protected override readonly schema = addStickerSchema;
    protected override readonly toolName = TOOL_NAMES.addSticker;
    protected override readonly toolDescription = 'Add a sticker to a scene';

    public override customRenderer = AddStickerResultRenderer;

    public override getToolDisplayName(): string {
        return 'Adding sticker';
    }

    public get statusIcon(): string {
        return 'sentiment_satisfied';
    }

    public async execute(args: AddStickerArgs, context: ToolExecutionContext) {
        const { parentActionGroup } = context;

        try {
            const { sceneIndex: scene_index, sceneShortId } = resolveSceneHandle(
                this.luminary,
                this.getState(),
                args,
            );

            const scene = this.luminary.getSceneById(this.luminary.scenes[scene_index].id);
            if (!scene) {
                return `Error: No scene found ${sceneShortId}`;
            }

            // Prevent adding stickers to branding scenes
            if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
                return `Error: Cannot add stickers to branding scenes. Scene ${sceneShortId} is a branding scene and should not be modified with stickers.`;
            }

            // Add sticker asset to scene using setMediaAssetForSceneOperation
            const { setMediaAssetForScene } = makeOperationsCallable(
                { setMediaAssetForSceneOperation },
                this.luminary,
                this.dispatch,
                this._getState,
            );

            const mediaSourceData = ImmutableFromJS({
                type: 'video',
                video: DEFAULT_STICKER_SOURCE_DATA,
                image: null,
                history_index_user_ids: null,
            }) as unknown as ImmutableMap<SlideBackgroundModel>;

            setMediaAssetForScene({
                sceneId: scene.id,
                mediaSourceData,
                isSticker: true,
                systemTriggered: false,
                parentActionGroup,
            });

            return `Success: Added sticker to scene ${sceneShortId}`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error adding sticker: ${errorMessage}`;
        }
    }
}

const AddStickerResultRenderer: React.FC<CustomRendererComponentProps> = ({
    isLoading,
    result,
}) => {
    if (isLoading) {
        return null;
    }
    return (
        <div className={chatMessageTextStyles['chat-message-text']}>
            <MarkdownWithDiff>{result}</MarkdownWithDiff>
        </div>
    );
};
